import { format } from "date-fns";
import { id } from "date-fns/locale";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

import { Header } from "../../components";
import { useAuth } from "../../context/AuthContext";
import { rekapPresensiUrl } from "../../helpers/ApiPresensi";
import { decryptLocalStorage } from "../../helpers/SecureStorage";
import { showError, showSessionExpired } from "../../helpers/Toast";

const RekapPresensi = () => {
  const { handleSessionExpired } = useAuth();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [rekapList, setRekapList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const token = decryptLocalStorage(process.env.REACT_APP_LOCAL_STORAGE_TOKEN);

  const getRekapPresensi = async (periode_awal, periode_akhir) => {
    const body = {
      periode_awal: format(periode_awal, "yyyy-MM"),
      periode_akhir: format(periode_akhir, "yyyy-MM"),
    };

    try {
      setIsLoading(true);
      const response = await fetch(rekapPresensiUrl, {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleSessionExpired();
          await showSessionExpired();
          navigate("/");
          return;
        }
        showError("Gagal memuat rekap presensi");
        return;
      }

      const data = await response.json();
      setRekapList(data.data || []);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const onFilter = (date, type) => {
    if (type === "awal") {
      setStartDate(date);
      getRekapPresensi(date, endDate);
    } else {
      setEndDate(date);
      getRekapPresensi(startDate, date);
    }
  };

  useEffect(() => {
    getRekapPresensi(startDate, endDate);
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Helmet>
        <title>Rekap Presensi</title>
      </Helmet>

      <div
        className="w-full px-4 pt-6 pb-14"
        style={{ background: "linear-gradient(135deg, #FFB703 0%, #e6a200 100%)" }}
      >
        <Header to="/home" title="Rekap Presensi" />
      </div>

      <div className="flex-grow px-4 pb-6" style={{ marginTop: -40 }}>
        {/* Filter periode */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-xs text-gray-400 mb-1 font-medium">Periode Awal</p>
              <DatePicker
                className="w-full text-sm font-semibold text-gray-700 bg-gray-50 rounded-xl px-3 py-2 focus:outline-none border border-gray-200"
                selected={startDate}
                onChange={(date) => onFilter(date, "awal")}
                dateFormat="MM/yyyy"
                locale={id}
                showMonthYearPicker
                showFullMonthYearPicker
              />
            </div>
            <div className="text-gray-400 font-bold text-sm mt-4">—</div>
            <div className="flex-1">
              <p className="text-xs text-gray-400 mb-1 font-medium">Periode Akhir</p>
              <DatePicker
                className="w-full text-sm font-semibold text-gray-700 bg-gray-50 rounded-xl px-3 py-2 focus:outline-none border border-gray-200"
                selected={endDate}
                onChange={(date) => onFilter(date, "akhir")}
                dateFormat="MM/yyyy"
                locale={id}
                showMonthYearPicker
                showFullMonthYearPicker
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl h-36 animate-pulse" />
            ))}
          </div>
        ) : rekapList.length > 0 ? (
          rekapList.map((item, index) => <RekapCard key={index} item={item} />)
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-10 text-center">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm text-gray-400">Rekap presensi tidak ditemukan</p>
          </div>
        )}
      </div>
    </div>
  );
};

const statItems = [
  { key: "jumlah_hadir", label: "Hadir", bg: "#D1FAE5", color: "#065F46" },
  { key: "jumlah_tepat", label: "Tepat", bg: "#DBEAFE", color: "#1E40AF" },
  { key: "jumlah_telat", label: "Telat", bg: "#FEE2E2", color: "#991B1B" },
  { key: "jumlah_izin", label: "Izin", bg: "#FEF3C7", color: "#92400E" },
  { key: "jumlah_dl", label: "Dinas", bg: "#EDE9FE", color: "#5B21B6" },
  { key: "jumlah_alpa", label: "Alpa", bg: "#FEE2E2", color: "#991B1B" },
];

const RekapCard = ({ item }) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-4 mb-3">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-bold text-gray-800">Rekap Bulan</span>
        <span
          className="text-xs font-bold px-3 py-1 rounded-full"
          style={{ backgroundColor: "#FFF3CD", color: "#92400E" }}
        >
          {`${item.namabulan} ${item.tahun}`}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {statItems.map(({ key, label, bg, color }) => (
          <div
            key={key}
            className="flex flex-col items-center justify-center rounded-xl py-2.5"
            style={{ backgroundColor: bg }}
          >
            <span className="text-base font-bold" style={{ color }}>{item[key] ?? 0}</span>
            <span className="text-xs font-medium mt-0.5" style={{ color }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RekapPresensi;
